import { ChangeEvent, Dispatch, SetStateAction, useState } from "react"
import { Box, Button, IconButton, TextField } from "@mui/material"
import { Add as AddIcon, Check as CheckIcon, Download as DownloadIcon } from "@mui/icons-material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { generateTaskReport } from "./utils/exports"
import { Task } from "./types"
import debounce from "../../../helpers/debounce"
import { useAppSelector } from '../../../redux/store'
import { selectTaskLogs, selectTasks } from "./slice"
import useTeamMembers from "../../../hooks/useTeamMembers"

type TaskButtonGroup = {
  tabIndex: number,
  selectedTasks: Map<number, Task>,
  setSearch: Dispatch<SetStateAction<string>>,
  setTaskModalOpen: Dispatch<SetStateAction<boolean>>,
  handleCompleteTasks: (tasks: Task[]) => Promise<void>,
}

const TaskButtonGroup = (props: TaskButtonGroup) => {
  const {
    tabIndex,
    selectedTasks,
    setSearch,
    setTaskModalOpen,
    handleCompleteTasks,
  } = props

  const tasks = useAppSelector(selectTasks)
  const taskLogs = useAppSelector(selectTaskLogs)
  const users = useTeamMembers()

  const [downloading, setDownloading] = useState(false)

  const handleDownload = async () => {
    setDownloading(true)
    await generateTaskReport(tasks, taskLogs, users)
    setDownloading(false)
  }

  const handleAddTask = () => {
    setTaskModalOpen(true)
  }

  const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const searchValue = event.target.value
    setSearch(searchValue)
  }

  const debouncedHandleSearch = debounce(handleSearch, 50)

  return (
    <Box sx={{ width: "650px" }}>
      <Box className="flex flex-space-between m-b-16" sx={{ height: "40px" }}>
        <Button
          variant='contained'
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={downloading}
        >
          Download Tasks
        </Button>
        {
          tabIndex === 0 && (
            <>
              <Button
                onClick={handleAddTask}
                variant='contained'
                startIcon={<AddIcon />}
              >
                New Task
              </Button>
              <TextField
                sx={{ minWidth: 200 }}
                size="small"
                label="Search"
                onChange={debouncedHandleSearch}
                InputProps={
                  {
                    endAdornment: (
                      <IconButton>
                        <FontAwesomeIcon icon="magnifying-glass" size="sm" />
                      </IconButton>
                    )
                  }
                }
              />
            </>
          )
        }
      </Box>
      {
        tabIndex === 0 && (
          <Box className="flex flex-end m-b-16">
            <Button
              sx={{
                backgroundColor: '#27c24c',
                '&:hover': {
                  backgroundColor: '#23ad44'
                }
              }}
              variant='contained'
              startIcon={<CheckIcon />}
              onClick={async () => {
                const selected = Array.from(selectedTasks, ([_, task]) => task)
                await handleCompleteTasks(selected)
              }}
              disabled={selectedTasks.size === 0}
            >
              Mark as Completed
            </Button>
          </Box>
        )
      }
    </Box>
  )
}

export default TaskButtonGroup
